import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alignment,
  Button,
  ButtonGroup,
  Card,
  Divider,
  H6,
  NavbarDivider,
  NavbarGroup,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
  RadioGroup,
  Radio,
  Switch,
  Tooltip,
} from "@blueprintjs/core";
import { useAuthActions } from "context/auth";
import {
  useSinkState,
  useSupportActions,
  useUnitState,
  useUnitActions,
} from "context";
import ApiUser from "../../api/user";
import { css } from "@emotion/core";

const NavButton = ({ to, ...props }: { to?: string; [key: string]: any }) => {
  const navigate = useNavigate();

  if (to) {
    return <Button {...props} onClick={() => navigate(to)} />;
  }

  return <Button {...props} />;
};

const AuthenticatedHeader = () => {
  const { logout } = useAuthActions();
  const { open: openSupport } = useSupportActions();
  const {
    sink: { user },
  } = useSinkState();
  const { unit } = useUnitState();
  const { setUnit } = useUnitActions();

  const [notifications, setNotifications] = React.useState<{
    notify_order_started: boolean;
    notify_order_complete: boolean;
    notify_survey_locked: boolean;
  }>({
    notify_order_started: user.notify_order_started,
    notify_order_complete: user.notify_order_complete,
    notify_survey_locked: user.notify_survey_locked,
  });

  const updateNotification = async (fields: {
    notify_order_started?: boolean;
    notify_order_complete?: boolean;
    notify_survey_locked?: boolean;
  }) => {
    const response = await ApiUser.updateUser(fields);

    if (response.ok === false) {
      setNotifications((prev) => ({
        ...prev,
        ...Object.entries(fields).reduce((flipped, [key, value]) => {
          flipped[key] = !value;
          return flipped;
        }, {}),
      }));
    }
  };

  const userMenuContent = (
    <Card style={{ minWidth: 220, padding: "0.5rem" }}>
      <div
        css={css`
          padding: 0.5rem;
        `}
      >
        <H6 style={{ marginTop: 0, marginBottom: "1rem" }}>Notifications</H6>
        <Switch
          checked={notifications.notify_order_started}
          label="Order Started"
          onChange={(e) => {
            setNotifications((prev) => ({
              ...prev,
              notify_order_started: e.target.checked,
            }));
            updateNotification({
              notify_order_started: e.target.checked,
            });
          }}
        />
        <Switch
          checked={notifications.notify_order_complete}
          label="Order Complete"
          onChange={(e) => {
            setNotifications((prev) => ({
              ...prev,
              notify_order_complete: e.target.checked,
            }));
            updateNotification({
              notify_order_complete: e.target.checked,
            });
          }}
        />
        <Switch
          checked={notifications.notify_survey_locked}
          label="Survey Locked"
          onChange={(e) => {
            setNotifications((prev) => ({
              ...prev,
              notify_survey_locked: e.target.checked,
            }));
            updateNotification({
              notify_survey_locked: e.target.checked,
            });
          }}
        />
        <RadioGroup
          label="Units"
          css={css`
            margin-top: 1.5rem;
          `}
          inline
          onChange={(event) => setUnit(event.target.value)}
          selectedValue={unit}
        >
          <Radio label="Metric" value="metric" />
          <Radio label="USCS" value="sae" />
        </RadioGroup>
      </div>
      <Button
        fill
        variant="minimal"
        alignText="left"
        icon="log-out"
        text="Logout"
        onClick={logout}
      />
    </Card>
  );

  return (
    <NavbarGroup align={Alignment.END}>
      <>
        <ButtonGroup>
          <Tooltip
            content="Knowledge Base"
            minimal
            position={PopoverPosition.BOTTOM}
          >
            <NavButton
              to="/knowledge"
              icon="manual"
              aria-label="Knowledge Base"
            />
          </Tooltip>
          <Tooltip
            content="Open a new support request"
            minimal
            position={PopoverPosition.BOTTOM}
          >
            <NavButton
              onClick={openSupport}
              icon="new-comment"
              text="New Request"
            />
          </Tooltip>
        </ButtonGroup>
        <NavbarDivider />
        <ButtonGroup>
          {(user?.organization_role === "admin" ||
            user?.organization_role === "fybr_admin") && (
            <NavButton
              to="/admin"
              icon="panel-table"
              text="Manage Organization"
            />
          )}
          {user?.organization_role === "fybr_admin" && (
            <NavButton to="/internal" icon="panel-table" text="FYBR Admin" />
          )}
        </ButtonGroup>
        {(user?.organization_role === "admin" ||
          user?.organization_role === "fybr_admin") && <NavbarDivider />}
        {user && (
          <Popover
            interactionKind={PopoverInteractionKind.CLICK}
            position={PopoverPosition.BOTTOM_RIGHT}
            content={userMenuContent}
          >
            <Button
              variant="minimal"
              icon="user"
              endIcon="caret-down"
              text={user.name}
            />
          </Popover>
        )}
      </>
    </NavbarGroup>
  );
};
export default AuthenticatedHeader;
